/**
 * セットアップウィザード。
 * 仕様書: docs/01_spec/spec_common.md C-4.2 (初回セットアップ)
 *
 * 表示条件:
 *   - user.role === "owner"
 *   - 必須シークレット (ANTHROPIC / LINE Channel Secret / LINE Channel Access Token) が未設定
 *
 * 表示内容:
 *   - ダッシュボード上部にバナーを表示し、設定画面のシークレット管理タブへ誘導
 *   - 必須シークレットが全て揃ったら自動で消える
 */
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useSecretsStatus, SECRET_KEYS } from '@/hooks/useSettings';
import { paths } from '@/routes/paths';

const REQUIRED_FOR_LAUNCH = ['anthropicApiKey', 'lineChannelSecret', 'lineChannelAccessToken'] as const;

export function SetupWizard() {
  const { user } = useAuth();
  const { data: statuses, isLoading } = useSecretsStatus();

  if (user?.role !== 'owner') return null;
  if (isLoading) return null;

  const missing = REQUIRED_FOR_LAUNCH.filter((key) => !statuses?.[key]?.configured);
  if (missing.length === 0) return null;

  const missingLabels = missing
    .map((k) => SECRET_KEYS.find((s) => s.key === k)?.label)
    .filter(Boolean);

  return (
    <div className="mb-6 rounded bg-[var(--accent-amber-subtle)] border border-[var(--accent-amber)] p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="size-5 text-[var(--accent-amber)] flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-bold text-[var(--text-primary)] mb-1">
            初回セットアップが必要です
          </h3>
          <p className="text-xs text-[var(--text-secondary)] mb-3 leading-relaxed">
            以下のシークレットが未登録のため LINE Bot が利用できません。
            設定画面のシークレット管理タブから登録してください。
          </p>
          <ul className="list-disc list-inside text-xs text-[var(--text-secondary)] mb-4 space-y-0.5">
            {missingLabels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
          <Link to={paths.settings}>
            <Button type="button" variant="primary" size="sm">
              シークレット管理へ
              <ArrowRight />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
